import FullScreenDialog from "../../FullScreenDialog";
import { WfButton, WfIconNavigationCloseRegular } from "@WahooFitness/redesignr";
import { t, Trans } from "@lingui/macro";
import { FitnessProfileResponseType } from "@WahooFitness/cloud-client-types";
import WfActionGroup from "@WahooFitness/redesignr/dist/Actions/WfActionGroup/WfActionGroup.js";
import useManualRunningProfileUpdate from "./useManualRunningProfileUpdate";
import {
  Box,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import DurationInput from "@/components/MoveToRedesignr/DurationInput";
import { useCallback, useMemo } from "react";
import { scrollIntoViewAfterKeyboard } from "@/hooks/useScrollIntoViewOnFocus";

const ManualRunningProfileUpdateDialog = ({
  fitnessProfile,
  isOpen,
  handleClose,
}: {
  fitnessProfile: FitnessProfileResponseType | undefined;
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const formFields = useMemo(
    () => [
      { key: "dur_1", label: t`5k`, min: 750, max: 9000, placeholder: "00:00" },
      { key: "dur_2", label: t`10k`, min: 1500, max: 18000, placeholder: "00:00" },
      { key: "dur_3", label: t`Half-marathon`, min: 3400, max: 38000, placeholder: "0:00:00" },
      { key: "dur_4", label: t`Marathon`, min: 7200, max: 76000, placeholder: "0:00:00" },
    ],
    []
  );

  const fieldValidations = useMemo(
    () => Object.fromEntries(formFields.map((f) => [f.key, { min: f.min, max: f.max }])),
    [formFields]
  );

  const {
    isSaving,
    formValues,
    fieldErrors,
    handleInputChange,
    handleBlur,
    handleReset,
    handleSave,
    isSaveButtonDisabled,
  } = useManualRunningProfileUpdate({
    fitnessProfile,
    handleClose,
    fieldValidations,
  });

  const hasAnyValue = useMemo(
    () => Object.values(formValues).some((val) => val !== null),
    [formValues]
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    scrollIntoViewAfterKeyboard(e.currentTarget);
  }, []);

  const { breakpoints, palette } = useTheme();
  return (
    <FullScreenDialog
      backgroundColor={palette.background.level1}
      slideDirection="up"
      edgeToEdge
      headerProps={{
        backgroundColorOverride: palette.background.level1,

        title: t`Update manually`,
        primaryLeftActionItem: {
          action: handleClose,
          icon: <WfIconNavigationCloseRegular />,
        },
      }}
      open={isOpen}
      handleClose={handleClose}
    >
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        bgcolor="background.level1"
        minHeight="600px"
        alignItems="center"
      >
        <Box display="flex" flexDirection="column" px={2} pt={1.5} rowGap={1.5}>
          <Typography variant="prose-base-medium">
            <Trans>
              By changing these values your Running Athlete Profile chart will be recalculated.
            </Trans>
          </Typography>

          <Stack spacing={1} width="100%" maxWidth="sm" mx="auto">
            {formFields.map((field) => (
              <Box
                key={field.key}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                bgcolor="secondary.light"
                p={1}
                borderRadius={2}
              >
                <Typography variant="ui-md-medium">{field.label}</Typography>
                <FormControl error={!!fieldErrors[field.key]}>
                  <OutlinedInput
                    name={field.key}
                    size="small"
                    value={formValues[field.key]}
                    onChange={handleInputChange}
                    onBlur={() => handleBlur(field.key)}
                    onFocus={handleFocus}
                    error={!!fieldErrors[field.key]}
                    inputProps={{ inputMode: "numeric" }}
                    inputComponent={DurationInput}
                    sx={{ maxWidth: 140 }}
                    placeholder={field.placeholder}
                  />
                  {fieldErrors[field.key] && (
                    <FormHelperText>{fieldErrors[field.key]}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            ))}
            <Typography variant="prose-sm-medium" color="text.secondary">
              <Trans>
                Enter time as mm:ss. Keep typing past 99 minutes and the field grows to h:mm:ss.
              </Trans>
            </Typography>
          </Stack>
        </Box>

        <Box width="100%" mt="auto" maxWidth="sm" py={2} display="flex" alignSelf="center">
          <WfButton
            hierarchy="text"
            intent="destructive"
            size="large"
            fullWidth
            onClick={handleReset}
            disabled={!hasAnyValue}
          >
            <Trans>Reset values</Trans>
          </WfButton>
        </Box>
        <WfActionGroup orientation="vertical" fullWidth sticky maxContentWidth="sm">
          <WfButton
            hierarchy="primary"
            intent="default"
            size="large"
            fullWidth
            onClick={handleSave}
            disabled={isSaveButtonDisabled}
            sx={{
              maxWidth: breakpoints.values.sm,
            }}
          >
            {isSaving ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
          </WfButton>
        </WfActionGroup>
      </Box>
    </FullScreenDialog>
  );
};

export default ManualRunningProfileUpdateDialog;
